const ADDED_TO_TRANSCRIPT_ARCHIVE_SCHEMA = 'attunewise.added_to_transcript_archive.v1'
const REPLY_TRANSCRIPT_COLLECTION_SCHEMA = 'attunewise.reply_transcript_collection.v1'

const isTranscriptArchiveEntry = entry => Boolean(
  entry &&
  entry.transcriptArchive &&
  entry.transcriptArchive.schema === ADDED_TO_TRANSCRIPT_ARCHIVE_SCHEMA &&
  entry.transcriptArchive.storagePath
)

const isTranscriptCollectionEntry = entry => Boolean(
  entry &&
  entry.transcriptCollection &&
  entry.transcriptCollection.schema === REPLY_TRANSCRIPT_COLLECTION_SCHEMA &&
  entry.transcriptCollection.path
)

// The storage path embeds a content digest — it is the version. updatedAt
// changes on every flush even when the content is identical, so it must not
// participate in cache identity.
const archiveCacheKey = archive => {
  if (!archive || !archive.storagePath) return ''
  return archive.storagePath
}

// Archives of the same message/model form a lineage: successive flushes write
// new digests of a growing prefix. The digest suffix is stripped so the most
// recent hydrated content of the lineage can stand in while a newer archive
// downloads — the reader sees yesterday's prefix for a beat, never a stub.
const archiveLineageKey = archive => {
  if (!archive || !archive.storagePath) return ''
  return 'lineage:' + String(archive.storagePath).replace(/-[0-9a-f]+\.json$/i, '')
}

const collectionCacheKey = pointer => {
  if (!pointer || !pointer.path) return ''
  return `${pointer.path}#${Number(pointer.count) || 0}`
}

const collectionLineageKey = pointer => pointer && pointer.path
  ? `collection:${pointer.path}`
  : ''

const transcriptPointer = entry => {
  if (isTranscriptArchiveEntry(entry)) {
    return {
      kind: 'archive',
      value: entry.transcriptArchive,
      key: archiveCacheKey(entry.transcriptArchive),
      lineageKey: archiveLineageKey(entry.transcriptArchive)
    }
  }
  if (isTranscriptCollectionEntry(entry)) {
    return {
      kind: 'collection',
      value: entry.transcriptCollection,
      key: collectionCacheKey(entry.transcriptCollection),
      lineageKey: collectionLineageKey(entry.transcriptCollection)
    }
  }
  return null
}

const transcriptArrays = message => {
  if (!message || message.role !== 'assistant') return []
  return [message].concat(message.models || [])
    .map(reply => reply && reply.addedToTranscript)
    .filter(Array.isArray)
}

const hasTranscriptArchives = message => transcriptArrays(message).some(entries => (
  entries.some(entry => isTranscriptArchiveEntry(entry) || isTranscriptCollectionEntry(entry))
))

const spliceArchiveEntry = (entries, index, replacement) => {
  entries.splice(index, 1, ...replacement)
  return replacement.length
}

const replaceCachedArchiveEntries = (message, cache) => {
  let changed = false
  for (const entries of transcriptArrays(message)) {
    for (let index = 0; index < entries.length;) {
      const entry = entries[index]
      const pointer = transcriptPointer(entry)
      if (!pointer) {
        index += 1
        continue
      }
      const cached = cache.get(pointer.key)
      if (!Array.isArray(cached)) {
        index += 1
        continue
      }
      index += spliceArchiveEntry(entries, index, cached)
      changed = true
    }
  }
  return changed
}

const loadTranscriptArchive = async ({ messageId, archive, cache, load }) => {
  const key = archiveCacheKey(archive)
  let cached = cache.get(key)
  if (!cached) {
    cached = Promise.resolve(load(messageId, archive)).then(entries => {
      const normalized = Array.isArray(entries) ? entries : []
      cache.set(key, normalized)
      cache.set(archiveLineageKey(archive), normalized)
      return normalized
    }).catch(err => {
      cache.delete(key)
      throw err
    })
    cache.set(key, cached)
  }
  return await cached
}

const loadTranscriptCollection = async ({ messageId, pointer, cache, loadCollection }) => {
  const key = collectionCacheKey(pointer)
  let cached = cache.get(key)
  if (!cached) {
    cached = Promise.resolve(loadCollection(messageId, pointer)).then(entries => {
      const normalized = Array.isArray(entries) ? entries : []
      cache.set(key, normalized)
      cache.set(collectionLineageKey(pointer), normalized)
      return normalized
    }).catch(err => {
      cache.delete(key)
      throw err
    })
    cache.set(key, cached)
  }
  return await cached
}

const hydrateTranscriptArchives = async (message, { cache, load, loadCollection }) => {
  let changed = replaceCachedArchiveEntries(message, cache)
  const pending = []
  const provisionalSlots = []
  for (const entries of transcriptArrays(message)) {
    for (let index = 0; index < entries.length;) {
      const entry = entries[index]
      const pointer = transcriptPointer(entry)
      if (!pointer) {
        index += 1
        continue
      }
      pending.push(pointer.kind === 'archive'
        ? loadTranscriptArchive({
            messageId: message.id,
            archive: pointer.value,
            cache,
            load
          })
        : loadTranscriptCollection({
            messageId: message.id,
            pointer: pointer.value,
            cache,
            loadCollection
          }))
      const provisional = cache.get(pointer.lineageKey)
      if (Array.isArray(provisional)) {
        // Show the lineage's previous content while the newer archive loads.
        const count = spliceArchiveEntry(entries, index, provisional)
        provisionalSlots.push({ entries, start: index, count, key: pointer.key })
        index += count
        changed = true
      } else {
        index += 1
      }
    }
  }
  if (pending.length > 0) {
    await Promise.all(pending)
    // Swap provisional content for the exact archive, innermost slots first
    // so earlier splices do not shift later offsets.
    for (const slot of provisionalSlots.sort((a, b) => b.start - a.start)) {
      const exact = slot.key && slot.entries && cache.get(slot.key)
      if (Array.isArray(exact)) {
        slot.entries.splice(slot.start, slot.count, ...exact)
        changed = true
      }
    }
    changed = replaceCachedArchiveEntries(message, cache) || changed
  }
  return changed
}

module.exports = {
  ADDED_TO_TRANSCRIPT_ARCHIVE_SCHEMA,
  REPLY_TRANSCRIPT_COLLECTION_SCHEMA,
  archiveCacheKey,
  archiveLineageKey,
  hasTranscriptArchives,
  hydrateTranscriptArchives,
  isTranscriptArchiveEntry,
  isTranscriptCollectionEntry,
  replaceCachedArchiveEntries
}
